/**
 * Batch 27h — Super-admin Tenant detail (rewrite).
 * B-24b WS-1 — contract fields (value, term start/end, account owner) added.
 *
 * Tabs: Overview / Users / Audit (state in URL query ``?tab=...``).
 * Overview surfaces the KPI strip + Edit + Suspend/Reinstate + Model
 * overrides drill-in + contract edit inline form. Users tab is the
 * load-bearing manual-verify surface (see
 * ``backend/docs/batch_27h_acceptance_addendum.md``).
 */
import { useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { EditTenantDialog } from "@/components/admin/tenants/EditTenantDialog";
import { ForceWipeTenantDialog } from "@/components/admin/tenants/ForceWipeTenantDialog";
import { ReinstateTenantDialog } from "@/components/admin/tenants/ReinstateTenantDialog";
import { SuspendTenantDialog } from "@/components/admin/tenants/SuspendTenantDialog";
import { TenantAuditTab } from "@/components/admin/tenants/TenantAuditTab";
import { TenantUsersTab } from "@/components/admin/tenants/TenantUsersTab";
import { TopUpCreditsDialog } from "@/components/admin/tenants/TopUpCreditsDialog";
import {
  useAdminTenant,
  useConvertToContracted,
  useUpdateTenant,
  useUpsertTenantContract,
} from "@/hooks/queries/admin/useAdminTenants";
import type { TenantDetail } from "@/hooks/queries/admin/useAdminTenants";

const CARD =
  "rounded-xl border border-[var(--p-slate-150)] bg-white p-4 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark";

type TabKey = "overview" | "users" | "audit";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "users", label: "Users" },
  { key: "audit", label: "Audit" },
];

function StatusPill({ status }: { status: string }): JSX.Element {
  const tone =
    status === "active"
      ? "bg-brand-positive/10 text-[var(--p-green-700)] ring-brand-positive/30 dark:bg-brand-positive/15 dark:text-[var(--p-green-350)]"
      : status === "suspended"
        ? "bg-yellow-100 text-yellow-800 ring-yellow-300 dark:bg-yellow-500/10 dark:text-yellow-300 dark:ring-yellow-500/30"
        : status === "pending_deletion"
          ? "bg-red-100 text-red-800 ring-red-300 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30"
          : "bg-brand-100 text-[var(--p-ink-700)] ring-[var(--p-border-input-light)] dark:bg-white/[0.06] dark:text-brand-100/60 dark:ring-white/[0.08]";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs ring-1 ${tone}`}>
      {status}
    </span>
  );
}

/** Format cents as a USD dollar string, or "—" for null. */
function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export default function AdminTenantDetail(): JSX.Element {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") as TabKey | null;
  const activeTab: TabKey =
    tabFromUrl === "users" || tabFromUrl === "audit" ? tabFromUrl : "overview";

  const { data: tenant, isLoading, error } = useAdminTenant(tenantId);
  const [editing, setEditing] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [reinstating, setReinstating] = useState(false);
  const [toppingUp, setToppingUp] = useState(false);
  const [forceWiping, setForceWiping] = useState(false);
  const [editingContract, setEditingContract] = useState(false);

  if (isLoading)
    return (
      <div className="text-sm text-text-secondary dark:text-brand-100/70">
        Loading…
      </div>
    );
  if (error) {
    return (
      <div className="text-sm text-red-600 dark:text-red-400">
        Couldn't load tenant: {error.message}
      </div>
    );
  }
  if (!tenant)
    return (
      <div className="text-sm text-text-secondary dark:text-brand-100/70">
        Not found.
      </div>
    );

  const setTab = (next: TabKey) => {
    const sp = new URLSearchParams(searchParams);
    if (next === "overview") sp.delete("tab");
    else sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  };

  const isActive = tenant.status === "active";
  const isSuspended = tenant.status === "suspended";
  const isPendingDeletion = tenant.status === "pending_deletion";

  return (
    <div className="space-y-4">
      <div>
        <Link
          to="/tenants"
          className="text-sm text-accent-primary-hover hover:underline dark:text-accent-secondary"
        >
          ← Tenants
        </Link>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-text-primary dark:text-text-primary">
            <a
              href={`${import.meta.env.VITE_APP_BASE_URL}/${tenant.slug}/login`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline hover:text-accent-primary-hover dark:hover:text-accent-secondary"
            >
              {tenant.name}
            </a>
          </h1>
          <StatusPill status={tenant.status} />
          <span className="text-xs text-accent-primary dark:text-brand-100/40">
            {tenant.slug}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded border border-brand-200 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
          >
            Edit
          </button>
          {isActive ? (
            <button
              type="button"
              onClick={() => setSuspending(true)}
              className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
            >
              Suspend
            </button>
          ) : null}
          {isSuspended ? (
            <button
              type="button"
              onClick={() => setReinstating(true)}
              className="rounded bg-accent-primary-hover px-3 py-1.5 text-sm font-medium text-white hover:bg-surface-elevated dark:bg-accent-primary dark:hover:bg-accent-primary-hover"
            >
              Reinstate
            </button>
          ) : null}
          {!isPendingDeletion ? (
            <button
              type="button"
              onClick={() => setToppingUp(true)}
              className="rounded border border-brand-200 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
            >
              Top up credits
            </button>
          ) : null}
          <Link
            to={`/tenants/${tenant.id}/model-overrides`}
            className="rounded border border-brand-200 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
          >
            Model overrides
          </Link>
          <button
            type="button"
            onClick={() => setForceWiping(true)}
            className="rounded border border-red-700 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10"
          >
            Force-wipe
          </button>
        </div>
      </div>

      {isPendingDeletion && tenant.pending_deletion_at ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          Marked for deletion on{" "}
          {new Date(tenant.pending_deletion_at).toLocaleString()}. This tenant
          is in its 30-day grace window. Reversal lives in the
          ``tenant_deletion_reversal`` CLI.
        </div>
      ) : null}

      {/* Tab strip */}
      <div className="border-b border-brand-100 dark:border-white/[0.06]">
        <nav className="flex gap-1" aria-label="Tabs">
          {TABS.map((t) => {
            const isActiveTab = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={
                  isActiveTab
                    ? "border-b-2 border-accent-primary-hover px-4 py-2 text-sm font-medium text-text-secondary dark:border-accent-secondary dark:text-text-primary"
                    : "border-b-2 border-transparent px-4 py-2 text-sm font-medium text-accent-primary hover:text-text-secondary dark:text-brand-100/50 dark:hover:text-brand-100/80"
                }
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === "overview" ? (
        <div className="space-y-4">
          {/* KPI strip */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Kpi label="Users" value={tenant.user_count.toString()} />
            <Kpi label="Plan" value={tenant.plan?.name ?? "—"} />
            <Kpi
              label="Credits"
              value={tenant.current_credits.toLocaleString()}
            />
            <Kpi
              label="Created"
              value={new Date(tenant.created_at).toLocaleDateString()}
            />
            <Kpi
              label="Trial ends"
              value={
                tenant.trial_ends_at
                  ? new Date(tenant.trial_ends_at).toLocaleDateString()
                  : "—"
              }
            />
          </div>
          {/* Read-only details */}
          <dl
            className={`grid grid-cols-2 gap-x-4 gap-y-2 text-sm ${CARD}`}
          >
            <dt className="font-medium text-text-secondary dark:text-brand-100/70">
              Stripe customer
            </dt>
            <dd className="text-text-primary dark:text-text-primary">
              {tenant.stripe_customer_id ?? "—"}
            </dd>
            <dt className="font-medium text-text-secondary dark:text-brand-100/70">
              Website
            </dt>
            <dd className="text-text-primary dark:text-text-primary">
              {tenant.company_website ?? "—"}
            </dd>
            <dt className="font-medium text-text-secondary dark:text-brand-100/70">
              Max users
            </dt>
            <dd className="text-text-primary dark:text-text-primary">
              {tenant.max_users}{" "}
              <span className="text-xs text-accent-primary dark:text-brand-100/40">
                (default: 100, demo default: 5) — click Edit to change
              </span>
            </dd>
          </dl>

          {/* SA trial controls (2026-07-28) */}
          <TrialControls tenant={tenant} />

          {/* B-24b WS-1 — Contract section */}
          <div className={CARD}>
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-text-primary dark:text-text-primary">
                Contract
              </h2>
              <button
                type="button"
                onClick={() => setEditingContract((v) => !v)}
                className="rounded border border-brand-200 px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
              >
                {editingContract ? "Cancel" : "Edit contract"}
              </button>
            </div>

            {editingContract && tenantId ? (
              <ContractEditForm
                tenantId={tenantId}
                current={tenant.contract ?? null}
                onDone={() => setEditingContract(false)}
              />
            ) : (
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                <dt className="font-medium text-text-secondary dark:text-brand-100/70">
                  Contract value (ARR)
                </dt>
                <dd className="text-text-primary dark:text-text-primary">
                  {formatCurrency(tenant.contract?.contract_value_cents)}
                </dd>
                <dt className="font-medium text-text-secondary dark:text-brand-100/70">
                  Term start
                </dt>
                <dd className="text-text-primary dark:text-text-primary">
                  {tenant.contract?.contract_term_start ?? "—"}
                </dd>
                <dt className="font-medium text-text-secondary dark:text-brand-100/70">
                  Renewal date
                </dt>
                <dd className="text-text-primary dark:text-text-primary">
                  {tenant.contract?.contract_term_end ?? "—"}
                </dd>
                <dt className="font-medium text-text-secondary dark:text-brand-100/70">
                  Account owner
                </dt>
                <dd className="text-text-primary dark:text-text-primary">
                  {tenant.contract?.account_owner ?? "—"}
                </dd>
              </dl>
            )}
          </div>
        </div>
      ) : null}

      {activeTab === "users" && tenantId ? (
        <TenantUsersTab tenantId={tenantId} />
      ) : null}

      {activeTab === "audit" ? (
        <TenantAuditTab recentActivity={tenant.recent_activity} />
      ) : null}

      {editing ? (
        <EditTenantDialog tenant={tenant} onClose={() => setEditing(false)} />
      ) : null}
      {suspending ? (
        <SuspendTenantDialog
          tenant={tenant}
          onClose={() => setSuspending(false)}
        />
      ) : null}
      {reinstating ? (
        <ReinstateTenantDialog
          tenant={tenant}
          onClose={() => setReinstating(false)}
        />
      ) : null}
      {toppingUp ? (
        <TopUpCreditsDialog
          tenant={tenant}
          onClose={() => setToppingUp(false)}
        />
      ) : null}
      {forceWiping ? (
        <ForceWipeTenantDialog
          tenant={tenant}
          onClose={() => setForceWiping(false)}
        />
      ) : null}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className={CARD}>
      <div className="text-xs uppercase tracking-wide text-accent-primary dark:text-brand-100/50">
        {label}
      </div>
      <div className="mt-1 text-base font-semibold text-text-primary dark:text-text-primary">
        {value}
      </div>
    </div>
  );
}

// ── B-24b WS-1 — contract edit form ──────────────────────────────────────────

interface ContractSummary {
  contract_value_cents: number | null;
  contract_term_start: string | null;
  contract_term_end: string | null;
  account_owner: string | null;
}

interface ContractEditFormProps {
  tenantId: string;
  current: ContractSummary | null;
  onDone: () => void;
}

function ContractEditForm({
  tenantId,
  current,
  onDone,
}: ContractEditFormProps): JSX.Element {
  const upsert = useUpsertTenantContract(tenantId);

  // Dollar string ↔ cents conversion.
  const centsToDollars = (c: number | null): string =>
    c == null ? "" : String(Math.round(c / 100));

  const [valueDollars, setValueDollars] = useState(
    centsToDollars(current?.contract_value_cents ?? null),
  );
  const [termStart, setTermStart] = useState(
    current?.contract_term_start ?? "",
  );
  const [termEnd, setTermEnd] = useState(current?.contract_term_end ?? "");
  const [accountOwner, setAccountOwner] = useState(
    current?.account_owner ?? "",
  );

  const inputClass =
    "w-full rounded border border-brand-200 bg-white px-2 py-1.5 text-sm dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary";
  const labelClass =
    "mb-0.5 block text-xs font-medium text-text-secondary dark:text-brand-100/70";

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const dollarNum = valueDollars.trim() === "" ? null : Number(valueDollars);
    upsert.mutate(
      {
        contract_value_cents:
          dollarNum != null && !Number.isNaN(dollarNum)
            ? Math.round(dollarNum * 100)
            : null,
        contract_term_start: termStart.trim() || null,
        contract_term_end: termEnd.trim() || null,
        account_owner: accountOwner.trim() || null,
      },
      {
        onSuccess: () => {
          onDone();
        },
      },
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-3 space-y-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Contract value ($ / year)</label>
          <input
            type="number"
            min={0}
            step={1}
            placeholder="e.g. 24000"
            value={valueDollars}
            onChange={(e) => setValueDollars(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Account owner</label>
          <input
            type="text"
            maxLength={256}
            placeholder="e.g. Jane Smith"
            value={accountOwner}
            onChange={(e) => setAccountOwner(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Term start</label>
          <input
            type="date"
            value={termStart}
            onChange={(e) => setTermStart(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Renewal date (term end)</label>
          <input
            type="date"
            value={termEnd}
            onChange={(e) => setTermEnd(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
      {upsert.isError ? (
        <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {upsert.error.message}
        </div>
      ) : null}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={upsert.isPending}
          className="rounded bg-accent-primary-hover px-3 py-1.5 text-xs font-medium text-white hover:bg-surface-elevated disabled:opacity-50 dark:bg-accent-primary dark:hover:bg-accent-primary-hover"
        >
          {upsert.isPending ? "Saving…" : "Save contract"}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded border border-brand-200 px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── SA trial controls (2026-07-28) ───────────────────────────────────────────
// Founder ask: self-serve signups auto-get a 45-day trial, but the SA console
// had no control. This block lets an SA extend/set the trial end date, turn the
// activation/trial drip emails off per tenant, and convert a trial to a
// contracted customer (clears the trial + moves the contract to 'active').
function TrialControls({ tenant }: { tenant: TenantDetail }): JSX.Element {
  const update = useUpdateTenant(tenant.id);
  const convert = useConvertToContracted(tenant.id);

  const isoToDateInput = (iso: string | null): string =>
    iso ? new Date(iso).toISOString().slice(0, 10) : "";
  const [dateStr, setDateStr] = useState(isoToDateInput(tenant.trial_ends_at));

  const contracted = tenant.contract?.lifecycle_state === "active";
  const busy = update.isPending || convert.isPending;
  const err = update.error ?? convert.error;

  const btnPrimary =
    "rounded bg-accent-primary-hover px-3 py-1.5 text-xs font-medium text-white hover:bg-surface-elevated disabled:opacity-50 dark:bg-accent-primary dark:hover:bg-accent-primary-hover";
  const btnDanger =
    "rounded border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/40 dark:text-red-300 dark:hover:bg-red-500/10";

  function saveDate(): void {
    if (!dateStr) return;
    // Set to end of the chosen day (UTC) so the trial covers that date.
    update.mutate({ trial_ends_at: new Date(`${dateStr}T23:59:59Z`).toISOString() });
  }

  function toggleDrip(): void {
    update.mutate({ trial_drip_enabled: !tenant.trial_drip_enabled });
  }

  function onConvert(): void {
    if (
      window.confirm(
        "Clear this tenant's trial and mark it as a contracted customer? " +
          "It will stop receiving trial drip emails and show no expiry. " +
          "(This does not charge or move any credits.)",
      )
    ) {
      convert.mutate();
    }
  }

  return (
    <div className={CARD}>
      <h2 className="text-sm font-semibold text-text-primary dark:text-text-primary">
        Trial
      </h2>
      <p className="mt-1 text-xs text-text-secondary dark:text-brand-100/60">
        {contracted
          ? "Contracted customer — not on a trial."
          : tenant.trial_ends_at
            ? `Trial ends ${new Date(tenant.trial_ends_at).toLocaleDateString()}.`
            : "No trial set."}
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-xs font-medium text-text-secondary dark:text-brand-100/70">
            Trial end date
          </label>
          <input
            type="date"
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            className="mt-1 rounded border border-brand-200 bg-white px-2 py-1.5 text-sm text-text-primary dark:border-white/[0.08] dark:bg-surface-card dark:text-text-primary"
          />
        </div>
        <button
          type="button"
          onClick={saveDate}
          disabled={busy || !dateStr || dateStr === isoToDateInput(tenant.trial_ends_at)}
          className={btnPrimary}
        >
          {update.isPending ? "Saving…" : "Set / extend"}
        </button>
      </div>

      <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-text-primary dark:text-text-primary">
        <input
          type="checkbox"
          checked={tenant.trial_drip_enabled}
          onChange={toggleDrip}
          disabled={busy}
        />
        Send activation / trial drip emails to this tenant
      </label>

      <div className="mt-3">
        <button type="button" onClick={onConvert} disabled={busy || contracted} className={btnDanger}>
          {convert.isPending
            ? "Converting…"
            : contracted
              ? "Already contracted"
              : "Clear trial & convert to contracted"}
        </button>
      </div>

      {err && (
        <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {err.message ?? "Update failed."}
        </div>
      )}
    </div>
  );
}
